import { useState } from "react";
import { ArrowDown, ArrowUp, ChevronRight, Plus, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { RichEditor } from "@/components/RichEditor";
import { JourneyEditor, QuoteForm, QuotesEditor, RelationshipsEditor, TimelineEditor, TropesEditor } from "@/components/BlockEditors";
import { htmlToText } from "@/lib/richtext";
import { KIND_LABEL, PRESETS, TOP_IDS, blockEmpty, makeBlock, newId, quoteEmpty, relEntries } from "@/lib/blocks";

const ib = "p-1.5 rounded-md text-slate-500 hover:text-slate-100 hover:bg-veil/[0.05] disabled:opacity-30 transition-colors";
const words = (html) => (htmlToText(html).match(/\S+/g) || []).length;
const countOf = (b, sections) => ({
  text: `${words(b.html)} words`, journey: `${b.tabs.length} tabs`, timeline: `${b.points.length} points`,
  relationships: `${relEntries({ sections }).reduce((n, g) => n + g.items.length, 0)} entries`, tropes: `${b.tropes.length} tropes`, quotes: `${b.quotes.length} quotes`,
}[b.kind]);

const Panel = ({ title, meta, open, setOpen, actions, children, testId }) => (
  <div className="rounded-xl border border-line bg-panel/50" data-testid={testId}>
    <div className="flex items-center gap-2 px-3 py-2">
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 flex-1 min-w-0 text-left" data-testid={`${testId}-toggle`}>
        <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-90" : ""}`} />
        <span className="font-display text-xl text-amber-50 truncate">{title}</span>
        <span className="font-code text-[10px] text-slate-500 shrink-0">{meta}</span>
      </button>
      {actions}
    </div>
    {open && <div className="px-3 pb-3">{children}</div>}
  </div>
);

const BlockPanel = ({ b, idx, blocks, setBlocks, ctx }) => {
  const [open, setOpen] = useState(b.id === "general" || ctx.fresh === b.id || !blockEmpty(b, { sections: ctx.sections }));
  const [confirm, setConfirm] = useState(false);
  const set = (patch) => setBlocks(blocks.map((x) => (x.id === b.id ? { ...x, ...patch } : x)));
  const movable = !TOP_IDS.includes(b.id);
  const firstMovable = blocks.findIndex((x) => !TOP_IDS.includes(x.id));
  const remove = () => (blockEmpty(b, { sections: ctx.sections }) || confirm ? setBlocks(blocks.filter((x) => x.id !== b.id)) : setConfirm(true));
  const props = { b, set, pages: ctx.pages };
  const body = {
    text: <RichEditor compact testId={`block-text-${b.id}`} value={b.html} pages={ctx.pages} onChange={(html) => set({ html })} />,
    journey: <JourneyEditor {...props} />, timeline: <TimelineEditor {...props} />, tropes: <TropesEditor {...props} />,
    relationships: <RelationshipsEditor {...props} sections={ctx.sections} setSections={ctx.setSections} />,
    quotes: <QuotesEditor {...props} onPin={ctx.setPinned} />,
  }[b.kind];
  const actions = (
    <div className="flex items-center gap-0.5 shrink-0">
      {movable && <button type="button" disabled={idx <= firstMovable} onClick={() => { const a = [...blocks]; [a[idx - 1], a[idx]] = [a[idx], a[idx - 1]]; setBlocks(a); }} className={ib} data-testid={`block-up-${b.id}`}><ArrowUp className="w-4 h-4" /></button>}
      {movable && <button type="button" disabled={idx === blocks.length - 1} onClick={() => { const a = [...blocks]; [a[idx + 1], a[idx]] = [a[idx], a[idx + 1]]; setBlocks(a); }} className={ib} data-testid={`block-down-${b.id}`}><ArrowDown className="w-4 h-4" /></button>}
      {confirm ? (
        <span className="flex items-center gap-1 text-xs">
          <button type="button" onClick={remove} className="text-rose-300 px-2 py-1 rounded bg-rose-500/10" data-testid={`block-remove-confirm-${b.id}`}>Remove with content</button>
          <button type="button" onClick={() => setConfirm(false)} className="text-slate-400 px-2 py-1">Keep</button>
        </span>
      ) : <button type="button" onClick={remove} className={`${ib} hover:text-rose-300`} title="Remove section" data-testid={`block-remove-${b.id}`}><Trash2 className="w-4 h-4" /></button>}
    </div>
  );
  return (
    <Panel title={b.title} meta={`${KIND_LABEL[b.kind]} · ${countOf(b, ctx.sections)}`} open={open} setOpen={setOpen} actions={actions} testId={`block-panel-${b.id}`}>
      {!b.preset && <Input value={b.title} maxLength={80} onChange={(e) => set({ title: e.target.value || " " })} className="h-9 mb-2 bg-canvas" placeholder="Section name" data-testid={`block-rename-${b.id}`} />}
      {body}
    </Panel>
  );
};

const AddSection = ({ blocks, setBlocks: put, character, onAdd }) => {
  const setBlocks = (next) => { const added = next.find((x) => !blocks.some((y) => y.id === x.id)); if (added) onAdd(added.id); put(next); };
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");
  const removed = character ? PRESETS.filter(([id]) => !blocks.some((b) => b.id === id)) : [];
  const readd = ([id, kind, title]) => {
    const blk = makeBlock(id, kind, title, true);
    const top = TOP_IDS.indexOf(id);
    if (top < 0) return setBlocks([...blocks, blk]);
    const at = blocks.filter((b) => TOP_IDS.indexOf(b.id) >= 0 && TOP_IDS.indexOf(b.id) < top).length;
    setBlocks([...blocks.slice(0, at), blk, ...blocks.slice(at)]);
  };
  const custom = () => { if (name.trim()) setBlocks([...blocks, makeBlock(newId(), "text", name.trim())]); setName(""); setNaming(false); };
  if (naming) return (
    <div className="flex gap-2">
      <Input autoFocus value={name} maxLength={80} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); custom(); } }} placeholder="Section name, e.g. Inventory" className="h-9 bg-canvas" data-testid="custom-section-name" />
      <button type="button" onClick={custom} className="h-9 px-4 rounded-full bg-amber-500 text-onaccent text-sm font-semibold" data-testid="custom-section-add">Add</button>
    </div>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="w-full h-11 rounded-xl border border-dashed border-line text-sm text-slate-400 hover:text-amber-300 hover:border-amber-500/40 flex items-center justify-center gap-2 transition-colors" data-testid="add-block-btn"><Plus className="w-4 h-4" /> Add section</button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-panel border-line w-64">
        {removed.map((p) => <DropdownMenuItem key={p[0]} onSelect={() => readd(p)} data-testid={`add-preset-${p[0]}`}>{p[2]}</DropdownMenuItem>)}
        {removed.length > 0 && <DropdownMenuSeparator />}
        <DropdownMenuItem onSelect={() => setNaming(true)} data-testid="add-custom-section">Custom text section…</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const BlocksEditor = ({ blocks, setBlocks, character, pinned, setPinned, pages, sections, setSections }) => {
  const [pinOpen, setPinOpen] = useState(!quoteEmpty(pinned));
  const [fresh, setFresh] = useState(null);
  const ctx = { pages, sections, setSections, fresh, setPinned: (q) => { setPinned(q); setPinOpen(true); } };
  const chars = pages.filter((p) => p.category === "Characters");
  return (
    <div className="space-y-3" data-testid="blocks-editor">
      {character && (
        <Panel title="Pinned quote" meta={quoteEmpty(pinned) ? "empty" : "pinned"} open={pinOpen} setOpen={setPinOpen} testId="block-panel-pinned"
          actions={!quoteEmpty(pinned) && <button type="button" onClick={() => setPinned(null)} className={`${ib} hover:text-rose-300`} data-testid="pinned-clear"><Trash2 className="w-4 h-4" /></button>}>
          <QuoteForm q={pinned || { said_to: { text: "", page_id: null }, lines: [] }} onChange={setPinned} chars={chars} testId="pinned-form" />
        </Panel>
      )}
      {blocks.map((b, i) => <BlockPanel key={b.id} b={b} idx={i} blocks={blocks} setBlocks={setBlocks} ctx={ctx} />)}
      <AddSection blocks={blocks} setBlocks={setBlocks} character={character} onAdd={setFresh} />
    </div>
  );
};
